import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import IntegrationAPI, {
  getPendingCompanyUuid,
  clearPendingCompanyUuid,
} from "lib/api/Integration";
import { CompanyContext } from "lib/context/CompanyContext";
import ProcoreCompanyPicker from "./ProcoreCompanyPicker";
import { isProcoreEnabled } from "./featureFlag";

export default function ProcoreCallback(props) {
  const history = useHistory();
  const { company } = useContext(CompanyContext);
  const { search } = useLocation();
  const started = useRef(false);
  const [phase, setPhase] = useState("working");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const companyUuid = useRef(getPendingCompanyUuid());

  useEffect(() => {
    // The code is single-use, so guard against a double effect run.
    if (started.current) return;
    started.current = true;

    if (!isProcoreEnabled(company?.company_user?.email)) {
      history.replace("/admin/field-configuration");
      return;
    }

    const params = new URLSearchParams(search);
    const code = params.get("code");
    const state = params.get("state");
    const denied = params.get("error");
    // Drop code/state from the URL and browser history.
    history.replace("/integrations/procore/callback");

    if (denied) {
      setError("Procore access was not granted.");
      setPhase("error");
      return;
    }
    if (!sessionStorage.getItem("token")) {
      history.replace("/login");
      return;
    }
    if (!code || !state || !companyUuid.current) {
      setError("This authorization link is incomplete or has expired.");
      setPhase("error");
      return;
    }

    IntegrationAPI.completeProcoreCallback(
      companyUuid.current,
      code,
      state
    ).then((res) => {
      clearPendingCompanyUuid();
      if (res.status === 401) {
        history.replace("/login");
        return;
      }
      if (res.status !== 200 || !res.data) {
        setError(res.message || "Could not complete the Procore connection.");
        setPhase("error");
        return;
      }
      if (res.data.status === "connected") {
        history.replace("/admin/field-configuration/integrations");
        return;
      }
      setResult(res.data);
      setPhase("pick");
    });
  }, [search, history]);

  const save = async (id) => {
    setSaving(true);
    const res = await IntegrationAPI.setProcoreCompany(companyUuid.current, id);
    setSaving(false);
    if (res.status === 200)
      history.replace("/admin/field-configuration/integrations");
    else setError(res.message || "Could not save the Procore company.");
  };

  return (
    <PageLayout {...props}>
      <div style={{ padding: "32px 24px", maxWidth: 640, margin: "0 auto" }}>
        <h2>Connect Procore</h2>
        {phase === "working" && <p>Completing the connection...</p>}
        {phase === "error" && (
          <>
            <p style={{ color: "#b00020" }}>{error}</p>
            <Link to="/admin/field-configuration/integrations">
              Back to Integrations
            </Link>
          </>
        )}
        {phase === "pick" && result && (
          <>
            {result.procore_user?.email && (
              <p>Signed in to Procore as {result.procore_user.email}.</p>
            )}
            {error && <p style={{ color: "#b00020" }}>{error}</p>}
            <ProcoreCompanyPicker
              companies={result.companies}
              saving={saving}
              onSave={save}
            />
          </>
        )}
      </div>
    </PageLayout>
  );
}
